import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { GROQButton } from "@/sanity/lib/definitions";
import { stegaClean } from "@sanity/client/stega";
import { Slot } from "@radix-ui/react-slot";
import { CTAWrapper } from "@/components/global/utils/CTAWrapper";
import { iconMap } from "@/lib/iconMap";

const buttonVariants = cva("button cta", {
  variants: {
    variant: {
      default: "button-green",
      outline: "button-outline",
    },
    action: {
      link: "",
      popup: "",
      anchor: "",
      none: "",
    },
  },
  defaultVariants: {
    variant: "default",
    action: "none",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, action, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const children = props.children;
    return (
      <Comp
        className={cn("group", buttonVariants({ variant, className }))}
        data-type={variant}
        data-action={action}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export interface CTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  data: GROQButton;
  date?: boolean;
  headerButton?: boolean;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  ({ className, data, date = false, headerButton = false, ...props }, ref) => {
    const cleanedData = stegaClean(data ?? {}) ?? null;

    const type = cleanedData?.type ?? "default";

    const hasIcon = cleanedData?.cta?.icon ?? "none";
    const icon = iconMap[hasIcon as keyof typeof iconMap];

    const action = cleanedData?.cta?.action ?? null;

    const currentYear = new Date().getFullYear().toString();
    const firstDigits = currentYear.slice(0, 2);
    const lastDigits = currentYear.slice(2, 4);

    const buttonElement = (
      <Button
        className="relative w-full group flex items-center justify-center gap-[1.25rem] overflow-clip"
        variant={type}
        action={action ?? "none"}
        ref={ref}
        {...props}
      >
        <div className="button-bg absolute top-0 left-0 w-full h-full group-hover:-translate-y-full transition-transform duration-500"/>
        <div className="button-bg-hover absolute top-0 left-0 w-full h-full translate-y-full group-hover:translate-y-0 transition-transform duration-500"/>

        <div className="relative leading-none overflow-clip">
          <div className="button-text group-hover:-translate-y-full transition-transform duration-500">
            {data?.cta ? (data.cta.title ?? "Please add a title") : "Please select a CTA"}
          </div>
          <div className="button-text-hover absolute top-0 left-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
            {data?.cta ? (data.cta.title ?? "Please add a title") : "Please select a CTA"}
          </div>
        </div>
        {/*{icon && (*/}
        {/*  <div className="button-icon relative transition-all duration-300 !w-[3rem] !min-w-[3rem] !h-[3rem] ">*/}
        {/*    {icon}*/}
        {/*  </div>*/}
        {/*)}*/}
      </Button>
    );

    return (
      <CTAWrapper
        cta={cleanedData?.cta}
        className={cn(
          "group inline-block relative z-10 w-full sm:w-auto",
          headerButton ? "min-w-[11.375rem]" : "min-w-[25.75rem]",
          className,
        )}
      >
        {date ? (
          <div className="flex items-center gap-[1rem]">
            <span className="w-[2rem] flex-shrink-0 text-[#B78768] font-instrument text-[2.25rem] flex items-center justify-center">
              {firstDigits}
            </span>
            {buttonElement}
            <span className="w-[2rem] flex-shrink-0 text-[#B78768] font-instrument text-[2.25rem] flex items-center justify-center">
              {lastDigits}
            </span>
          </div>
        ) : (
          buttonElement
        )}
      </CTAWrapper>
    );
  },
);
CTAButton.displayName = "CTAButton";

export { Button, buttonVariants, CTAButton };
